import React from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography,
  Grid, Checkbox, FormControlLabel, Box, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import Select from 'react-select';

// react-select renders its menu inside the dialog's scroll container, where
// it gets clipped; portal it to the body above MUI's dialog z-index (1300).
const selectStyles = {
  menu: (provided) => ({ ...provided, backgroundColor: 'white' }),
  menuPortal: (provided) => ({ ...provided, zIndex: 1400 }),
};

export const EMPTY_TEAM = { id: '', name: '', ranking: '', record: '' };

// Add/Edit form for one game. Controlled: `value` is the form state owned by
// GameManagement, and every field change goes back through onChange. In edit
// mode there is no week control — a game is always saved in the week it is in.
function GameFormDialog({ open, mode, week, teams, value, onChange, onSubmit, onClose, isSaving }) {
  const isEditing = mode === 'edit';
  const teamOptions = teams.map((team) => ({ value: team.id, label: team.name }));
  const theme = useTheme();
  // Full-screen on phones: the form is long, and this keeps Cancel/Save
  // pinned at the bottom while the fields scroll.
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleInputChange = (e) => {
    const { name, value: fieldValue, checked, type } = e.target;
    if (name.startsWith('team1') || name.startsWith('team2')) {
      const team = name.startsWith('team1') ? 'team1' : 'team2';
      const field = name.replace(team, '').toLowerCase();
      onChange({ ...value, [team]: { ...value[team], [field]: fieldValue } });
    } else if (type === 'checkbox') {
      onChange({ ...value, [name]: checked });
    } else {
      onChange({ ...value, [name]: fieldValue });
    }
  };

  const handleTeamChange = (option, team) => {
    onChange({
      ...value,
      [team]: { ...value[team], id: option?.value || '', name: option?.label || '' },
    });
  };

  const renderTeamColumn = (key, heading) => (
    <Grid size={{ xs: 12, md: 6 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>{heading}</Typography>
      <Select
        inputId={`${key}-team`}
        aria-label={`${heading} team`}
        styles={selectStyles}
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        options={teamOptions}
        value={value[key].id ? { value: value[key].id, label: value[key].name } : null}
        onChange={(option) => handleTeamChange(option, key)}
        placeholder="Select Team"
        isClearable
      />
      <TextField
        label="Team Ranking"
        name={`${key}ranking`}
        fullWidth
        value={value[key].ranking}
        onChange={handleInputChange}
        margin="normal"
        size="small"
      />
      <TextField
        label="Team Record"
        name={`${key}record`}
        fullWidth
        value={value[key].record}
        onChange={handleInputChange}
        margin="normal"
        size="small"
      />
    </Grid>
  );

  return (
    // The dialog's Paper *is* the form (not a <form> wrapped inside it), so the
    // content area keeps its constrained height and the actions stay visible.
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="md"
      fullScreen={isMobile}
      slotProps={{ paper: { component: 'form', onSubmit } }}
    >
        <DialogTitle>{isEditing ? `Edit game (Week ${value.week})` : `Add game to Week ${week}`}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            {renderTeamColumn('team1', 'Away Team')}
            {renderTeamColumn('team2', 'Home Team')}

            <Grid size={12}>
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Game Info</Typography>
              <Grid container spacing={2}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Game Date"
                    name="gameDate"
                    type="date"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={value.gameDate}
                    onChange={handleInputChange}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Game Time"
                    name="gameTime"
                    type="time"
                    fullWidth
                    slotProps={{ inputLabel: { shrink: true } }}
                    value={value.gameTime}
                    onChange={handleInputChange}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Vegas Betting Line"
                    name="bettingLine"
                    fullWidth
                    value={value.bettingLine}
                    onChange={handleInputChange}
                    margin="normal"
                    size="small"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    label="Neutral Site"
                    name="neutralSite"
                    fullWidth
                    value={value.neutralSite}
                    onChange={handleInputChange}
                    margin="normal"
                    size="small"
                  />
                </Grid>
              </Grid>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', columnGap: 3 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={value.isPickScore}
                      onChange={handleInputChange}
                      name="isPickScore"
                      color="primary"
                    />
                  }
                  label="Is Pick Score"
                />
                {value.isPickScore && (
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={value.awardWinnerBonus}
                        onChange={handleInputChange}
                        name="awardWinnerBonus"
                        color="primary"
                      />
                    }
                    label="Also award winner bonus (0.25 pts)"
                  />
                )}
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={isSaving}>Cancel</Button>
          <Button type="submit" variant="contained" color="primary" loading={isSaving}>
            {isEditing ? 'Update Game' : 'Add Game'}
          </Button>
        </DialogActions>
    </Dialog>
  );
}

export default GameFormDialog;
